import { useContext, useEffect, useMemo, useState } from "react";
import { convertDateStringToPtBrFormat, convertStringToCpnjFormat } from "../../utils/converterUtils";
import { FileContext } from "../../store/FileContextProvider";
import { LoaderContext } from "../../store/LoaderContextProvider";
import { ScannerContext } from "../../store/ScannerContextProvider";
import { BsFiletypeXml, BsUpcScan } from "react-icons/bs";
import CustomSnackBar from "../../components/CustomSnackBar/CustomSnackBar";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomToolBar from "../../components/CustomToolBar/CustomToolBar";
import LandingInfo from "../../components/LandingInfo/LandingInfo";
import InfoListCard from "../../components/InfoListCard/InfoListCard";
import CheckingStatusGroupPanel from "./components/CheckingStatusGroupPanel";
import { actionWithSpinnerLoading } from "../../utils/loaderUtils";
import FileDropContainer from "../../components/FileDropContainer/FileDropContainer";
import { useFileDropActions } from "../../hooks/useFileDropActions";
import { notaFiscalProduto } from "../../interfaces/notaFiscal/notaFiscalProduto";
import { isEmpty } from "../../utils/dataCheckUtils";
import * as Styles from "./Checking.styles";
import { StatusContext } from "../../store/StatusContextProvider";
import { isSameProduct } from "../../utils/notaFiscalUtils";

export default function Checking() {
  const { notaFiscalProductsArray, areXmlTagsValid, notaFiscalInformations, xmlFileImportsCount } = useContext(FileContext);
  const { setIsloading } = useContext(LoaderContext);
  const { scannerArray, updateScannerArray, handleFocusOnScanner, isScanOn } = useContext(ScannerContext);
  const { calculateCounters } = useContext(StatusContext);

  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);

  const { handleFileDropped } = useFileDropActions();

  const hasNotaFiscalProducts = !isEmpty<notaFiscalProduto>(notaFiscalProductsArray);
  const hasScannerData = !isEmpty<notaFiscalProduto>(scannerArray);

  // Atualizar dados do scannerArray ao importar XML
  const memoizedScannerArray = useMemo(() => {
    const xProdNotFoundText: string = "Produto inesperado";
    const uComNotFoundText: string = "----";

    if (notaFiscalProductsArray.length === 0) {
      return scannerArray;
    }

    const updatedArray = scannerArray.map((item) => {
      const product = notaFiscalProductsArray.find((xmlItem) => isSameProduct(xmlItem, item));

      let newXProd = item.xProd;

      if (!newXProd || newXProd === xProdNotFoundText) {
        newXProd = product ? product.xProd : xProdNotFoundText;
      }

      return {
        ...item,
        xProd: newXProd,
        uCom: product ? product.uCom : uComNotFoundText,
      };
    });

    return updatedArray;
  }, [notaFiscalProductsArray, scannerArray]);

  // Resetar scanner ao limpar
  function handleResetScanner() {
    if (!isScanOn || !hasScannerData) return;

    actionWithSpinnerLoading({
      handleAction: () => {
        updateScannerArray([]);
        calculateCounters([], [], true);
      },
      setIsLoading: setIsloading,
      handleFocus: handleFocusOnScanner,
    });
  }

  // Analisar tag ao importar xml
  useEffect(() => {
    if (!areXmlTagsValid) {
      setIsSnackBarOpen(true);
    }
  }, [areXmlTagsValid, xmlFileImportsCount]);

  function handleCloseSnackbar() {
    setIsSnackBarOpen(false);
  }

  return (
    <FileDropContainer onFileDropped={handleFileDropped}>
      <Styles.CustomCheckingMainContainer>
        <CustomToolBar />
        <CustomSnackBar message="Arquivo XML invalido!" onClose={handleCloseSnackbar} open={isSnackBarOpen} />
        {!isScanOn && !hasNotaFiscalProducts ? (
          <LandingInfo />
        ) : (
          <Styles.CustomCheckingContainer>
            <Styles.CustomCheckingHeader>
              {hasNotaFiscalProducts && notaFiscalInformations && (
                <Styles.CustomHeaderLeftCard>
                  <InfoListCard
                    key={notaFiscalInformations.nNf}
                    title="Informações da nota"
                    highLightLabel="Emitida"
                    highLightValue={convertDateStringToPtBrFormat(notaFiscalInformations.dhEmi)}
                    listItems={[{ label: "Número", value: notaFiscalInformations.nNf }]}
                    subSectionTitle="Fornecedor"
                    subSectionItems={[
                      { label: "Nome", value: notaFiscalInformations.xNome },
                      {
                        label: "CNPJ",
                        value: convertStringToCpnjFormat(notaFiscalInformations.cnpj),
                      },
                    ]}
                  />
                </Styles.CustomHeaderLeftCard>
              )}
              <CheckingStatusGroupPanel />
            </Styles.CustomCheckingHeader>

            <Styles.CustomBodyContainerTables>
              {hasNotaFiscalProducts && (
                <Styles.CustomScannerBodyTables>
                  <CustomTable
                    primaryArray={notaFiscalProductsArray}
                    secondaryArray={memoizedScannerArray}
                    tableTitle="Produtos no XML"
                    isGettingCounter={true}
                    TableIcon={BsFiletypeXml}
                  />
                </Styles.CustomScannerBodyTables>
              )}
              {isScanOn && (
                <Styles.CustomScannerBodyTables>
                  <CustomTable
                    primaryArray={memoizedScannerArray}
                    secondaryArray={notaFiscalProductsArray}
                    tableTitle="Produtos na conferência"
                    TableIcon={BsUpcScan}
                    isScanMode={isScanOn}
                    onResetScanner={handleResetScanner}
                  />
                </Styles.CustomScannerBodyTables>
              )}
            </Styles.CustomBodyContainerTables>
          </Styles.CustomCheckingContainer>
        )}
      </Styles.CustomCheckingMainContainer>
    </FileDropContainer>
  );
}
